// Plan 30-01 — useThemeAtlas: typed Texture refs for Runespring frames.
//
// Per UI-SPEC §2 sprite-hierarchy + plan §<interfaces>: LetterCubeSprite
// (Plan 30-02) reads 6 new atlas frames + the per-letter cube_letter_X
// frame. This module exposes them as a typed structure so consumers can't
// typo a frame name (TypeScript catches it) and so theme-change rebinds
// the same struct shape against the new atlas (caller swaps the
// descriptor → useMemo re-runs → new Texture refs).
//
// Wave 1 boundary: the actual `Texture.from(frameName)` lookup is provided
// by a factory function so this module stays Pixi-agnostic at test time.
// Plan 30-02 wires the real factory (Pixi `Assets.get(...).textures[...]`)
// when LetterCubeSprite mounts.
//
// Idempotency: `buildRuneAtlasTextures(descriptor, factory)` is a PURE
// function; the React-hook `useThemeAtlas` wraps it in `useMemo` keyed on
// the descriptor identity so a same-descriptor re-render returns the
// previous return value (sprite reconstruction not needed). Theme switch
// = new descriptor = new struct.

// Texture is imported as a TYPE only — we never construct one here. The
// runtime factory is supplied by the caller (Plan 30-02 provides the real
// Pixi-backed factory at LetterLayer mount).
import type { Texture } from 'pixi.js'
import { useMemo } from 'react'
import type { ThemeDescriptor } from './descriptor'

/**
 * Plan 30-01 — the 6 new Runespring frame names. Mirrors the atlas-frames
 * test gate + atlas.json append-block. Exported so consumers can iterate
 * (e.g. to preload all 6 textures in a single batch).
 */
export const REQUIRED_RUNE_FRAME_NAMES = [
  'tier_forged_ring',
  'tier_rare_inlay',
  'tier_mythic_glow',
  'anchor_sigil_rune',
  'hollow_cracked_rune_stone',
  'tint_crosshatch_pattern',
] as const

export type RuneFrameName = (typeof REQUIRED_RUNE_FRAME_NAMES)[number]

/**
 * Typed Texture refs for the 6 new Runespring overlays + a lazy lookup
 * for the per-letter cube_letter_X tile-base. LetterCubeSprite (Plan
 * 30-02) reads these directly; tile-base is a function (not a precomputed
 * map) because the active letter set per cell is dynamic.
 */
export interface RuneAtlasTextures {
  /** cube_letter_<letter> frame — lazy lookup since letters are dynamic. */
  tileBase: (letter: string) => Texture
  /** Forged-tier ring overlay (32×32). Sprite.tint'd to forged-ring-color. */
  tierForgedRing: Texture
  /** Rare-tier edge inlay overlay (32×32). Sprite.tint'd to rare-inlay-color. */
  tierRareInlay: Texture
  /** Mythic-tier soft glow (32×32 radial alpha). Sprite.tint'd to glow-color. */
  tierMythicGlow: Texture
  /** Anchor sigil rune (16×16 glyph). Sprite.tint'd to sigil-color-{a,b}. */
  anchorSigilRune: Texture
  /** Hollow cracked rune-stone (32×32). Sprite.tint'd to hollow-crack-color. */
  hollowCrackedRuneStone: Texture
  /** Crosshatch pattern (32×32 hatch). Layered above Player B standard tint. */
  tintCrosshatchPattern: Texture
}

/**
 * Pure core of useThemeAtlas — takes a Texture factory so unit tests stay
 * hermetic (no Pixi cache, no atlas bytes). React's useMemo wraps this in
 * the public hook below.
 *
 * The factory function receives a frame name (e.g. 'tier_forged_ring') and
 * returns the corresponding Texture. The real Plan-30-02 wiring calls
 * `Texture.from(frameName)` against the descriptor's atlas; tests pass a
 * vi.fn() that returns marker objects.
 *
 * @param _descriptor Used to key the useMemo cache; the descriptor itself
 *                    is opaque to this function (the factory holds the
 *                    binding to the actual atlas).
 * @param textureFactory Callable that returns a Texture for a frame name.
 */
export function buildRuneAtlasTextures(
  // Underscore prefix: descriptor is consumed by the useMemo cache key in
  // the public hook below, not by this pure function.
  _descriptor: ThemeDescriptor,
  textureFactory: (frameName: string) => unknown,
): RuneAtlasTextures {
  return {
    tileBase: (letter: string): Texture =>
      textureFactory(`cube_letter_${letter}`) as Texture,
    tierForgedRing: textureFactory('tier_forged_ring') as Texture,
    tierRareInlay: textureFactory('tier_rare_inlay') as Texture,
    tierMythicGlow: textureFactory('tier_mythic_glow') as Texture,
    anchorSigilRune: textureFactory('anchor_sigil_rune') as Texture,
    hollowCrackedRuneStone: textureFactory(
      'hollow_cracked_rune_stone',
    ) as Texture,
    tintCrosshatchPattern: textureFactory('tint_crosshatch_pattern') as Texture,
  }
}

/**
 * React-hook entry point. Returns the same `RuneAtlasTextures` object
 * reference for a stable descriptor (useMemo identity contract); a new
 * descriptor (theme switch) returns a fresh struct.
 *
 * @param descriptor    The active theme descriptor (from ThemeProvider).
 * @param textureFactory Callable that resolves frame names to Textures.
 *                       Plan 30-02 wires this to Pixi's `Texture.from` /
 *                       `Assets.get(atlas).textures[frameName]` pattern;
 *                       passing a custom factory is supported for tests.
 */
export function useThemeAtlas(
  descriptor: ThemeDescriptor,
  textureFactory: (frameName: string) => unknown,
): RuneAtlasTextures {
  // The factory MUST be stable across renders for the memo to be useful —
  // callers pin it via useCallback / module-scoped const. We include
  // `textureFactory` in the deps so a factory-swap (e.g. theme atlas
  // re-bind in P30-02) re-runs the build.
  return useMemo(
    () => buildRuneAtlasTextures(descriptor, textureFactory),
    [descriptor, textureFactory],
  )
}
